import React from 'react';
import useGameStore from '../../store/gameStore';
import useAudioStore from '../../store/audioStore';
import { formatMoney } from '../../utils/format';
import { BarChart3, TrendingUp, TrendingDown, Users, Truck, Star, X, Award, ThumbsUp, Wallet, PiggyBank, Banknote, Building2 } from 'lucide-react';
import { useTranslation } from '../../i18n';

const MonthlyReport = () => {
  const { t } = useTranslation();
  const { pendingMonthlyReport, dismissMonthlyReport, gameStarted } = useGameStore();
  const playSound = useAudioStore((s) => s.play);

  // Play monthly report sound when it appears
  React.useEffect(() => {
    if (gameStarted && pendingMonthlyReport) {
      playSound('monthlyReport');
    }
  }, [pendingMonthlyReport, gameStarted, playSound]);

  if (!gameStarted || !pendingMonthlyReport) return null;
  const r = pendingMonthlyReport;
  const MONTHS = ['', 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

  return (
    <div className="fixed inset-0 z-[85] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="monthly-report">
      <div className="bg-ink-800 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#1F3864] to-[#2D4A7A] px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-foreground">
            <BarChart3 className="w-5 h-5" />
            <div>
              <h3 className="font-bold text-base">Bilan mensuel</h3>
              <p className="text-xs text-blue-200">{MONTHS[r.month]} {r.year}</p>
            </div>
          </div>
          <button aria-label="Fermer"
            onClick={dismissMonthlyReport}
            className="text-foreground/60 hover:text-foreground transition-colors p-1"
            data-testid="close-monthly-report"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 space-y-4">
          {/* Revenue vs Expenses */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-money/10 rounded-xl p-3 text-center">
              <TrendingUp className="w-5 h-5 text-money mx-auto mb-1" />
              <p className="text-[10px] text-slate-500">{t('monthly_revenue_label')}</p>
              <p className="text-lg font-bold text-money">{formatMoney(r.totalRevenue)}</p>
            </div>
            <div className="bg-danger/10 rounded-xl p-3 text-center">
              <TrendingDown className="w-5 h-5 text-danger mx-auto mb-1" />
              <p className="text-[10px] text-slate-500">{t('monthly_expenses_label')}</p>
              <p className="text-lg font-bold text-danger">{formatMoney(r.totalExpenses)}</p>
            </div>
          </div>

          {/* Net result */}
          <div className={`rounded-xl p-4 text-center ${r.netResult >= 0 ? 'bg-money/15' : 'bg-danger/15'}`}>
            <p className="text-xs text-slate-400">{t('net_result_label')}</p>
            <p className={`text-2xl font-bold ${r.netResult >= 0 ? 'text-money' : 'text-danger'}`}>
              {r.netResult >= 0 ? '+' : ''}{formatMoney(r.netResult)}
            </p>
          </div>

          {/* Key metrics */}
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-ink-700/30 rounded-lg p-2.5 text-center">
              <Truck className="w-4 h-4 text-foreground mx-auto mb-0.5" />
              <p className="text-xs font-bold text-slate-300">{r.vehiclesActive}/{r.vehiclesTotal}</p>
              <p className="text-[10px] text-slate-500">{t('active_vehicles_label')}</p>
            </div>
            <div className="bg-ink-700/30 rounded-lg p-2.5 text-center">
              <Users className="w-4 h-4 text-foreground mx-auto mb-0.5" />
              <p className="text-xs font-bold text-slate-300">{r.driversTotal}</p>
              <p className="text-[10px] text-slate-500">Chauffeurs</p>
            </div>
            <div className="bg-ink-700/30 rounded-lg p-2.5 text-center">
              <Star className="w-4 h-4 text-warn mx-auto mb-0.5" />
              <p className="text-xs font-bold text-slate-300">{r.reputation}</p>
              <p className="text-[10px] text-slate-500">{t('reputation_label')}</p>
            </div>
          </div>

          {/* Best driver */}
          {r.bestDriverName !== '—' && (
            <div className="flex items-center gap-3 bg-accent/10 rounded-lg px-3 py-2.5">
              <Award className="w-5 h-5 text-accent flex-shrink-0" />
              <div>
                <p className="text-xs text-slate-500">{t('driver_of_month_label')}</p>
                <p className="text-sm font-bold text-foreground">{r.bestDriverName}</p>
              </div>
              <div className="ml-auto text-right">
                <p className="text-[10px] text-slate-500">Satisfaction</p>
                <p className="text-sm font-bold text-money">{r.bestDriverSatisfaction}%</p>
              </div>
            </div>
          )}

          {/* Treasury */}
          <div className="flex items-center justify-between px-2 py-1">
            <span className="text-xs text-slate-500">Trésorerie actuelle</span>
            <span className="text-sm font-bold text-foreground">{formatMoney(r.treasury)}</span>
          </div>

          {/* Patrimoine & Placements (P1#8 feedback 26 mai) */}
          <div className="border-t border-ink-600 pt-3 mt-2">
            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-2">Patrimoine & Capitaux</p>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="bg-ink-700/30 rounded-lg px-3 py-2 flex items-center justify-between" data-testid="report-equity">
                <span className="flex items-center gap-1 text-slate-400">
                  <Building2 className="w-3 h-3" /> Capitaux propres
                </span>
                <span className={`font-mono font-bold ${(r.equity || 0) >= 0 ? 'text-money' : 'text-danger'}`}>
                  {formatMoney(r.equity || 0)}
                </span>
              </div>
              <div className="bg-ink-700/30 rounded-lg px-3 py-2 flex items-center justify-between" data-testid="report-actif">
                <span className="flex items-center gap-1 text-slate-400">
                  <Wallet className="w-3 h-3" /> Actif total
                </span>
                <span className="font-mono text-slate-200">{formatMoney(r.actifTotal || 0)}</span>
              </div>
              {(r.placementsCount || 0) > 0 && (
                <div className="bg-emerald-500/10 rounded-lg px-3 py-2 flex items-center justify-between col-span-2" data-testid="report-placements">
                  <span className="flex items-center gap-1 text-emerald-300">
                    <PiggyBank className="w-3 h-3" /> Placements ({r.placementsCount})
                  </span>
                  <span className="font-mono text-emerald-300 font-bold">
                    {formatMoney(r.placementsCapital || 0)}
                    {(r.placementsReturn || 0) > 0 && (
                      <span className="text-[10px] text-money ml-1">(+{formatMoney(r.placementsReturn)})</span>
                    )}
                  </span>
                </div>
              )}
              {(r.totalDebt || 0) > 0 && (
                <div className="bg-rose-500/10 rounded-lg px-3 py-2 flex items-center justify-between col-span-2" data-testid="report-debt">
                  <span className="flex items-center gap-1 text-rose-300">
                    <Banknote className="w-3 h-3" /> Dette restante
                  </span>
                  <span className="font-mono text-rose-300">
                    {formatMoney(r.totalDebt)}
                    {(r.monthlyInterest || 0) > 0 && (
                      <span className="text-[10px] text-rose-400 ml-1">· agios {formatMoney(r.monthlyInterest)}/mois</span>
                    )}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-ink-600">
          <button
            onClick={dismissMonthlyReport}
            className="w-full bg-accent text-white py-2.5 rounded-lg text-sm font-bold hover:bg-accent-hover transition-colors flex items-center justify-center gap-2"
            data-testid="dismiss-monthly-report-btn"
          >
            <ThumbsUp className="w-4 h-4" /> Continuer
          </button>
        </div>
      </div>
    </div>
  );
};

export default MonthlyReport;
